import { config } from '@iheartjane/config';
import type { CardCarouselProps } from '@iheartjane/menu';
import { CardCarousel } from '@iheartjane/menu';
import type { AppMode, PriceId } from '@iheartjane/types';
import { useMemo } from 'react';

import { useSimilarProductsRow } from '@jane/dm/internal';
// eslint-disable-next-line no-restricted-imports
import type { Store } from '@jane/shared/models';

import { MenuProductCarousel } from '../menuProductCarousel/menuProductCarousel';
import { ProductCardWithTracking } from '../productCardWithTracking/productCardWithTracking';
import type { ProductCardWithTrackingProps } from '../productCardWithTracking/productCardWithTracking.types';

type ProductDetailsSponsoredCarouselProps = Pick<CardCarouselProps, 'mode'> &
  Pick<
    ProductCardWithTrackingProps,
    'cartProducts' | 'cartIsOpen' | 'cartStoreId' | 'specials'
  > & {
    appMode: AppMode;
    attemptToAddToCart: (...args: unknown[]) => void;
    isProductInStock: boolean;
    onDeleteFromCart: (id: number, priceId: PriceId | null) => void;
    productId: number;
    store: Store;
  };

export const ProductDetailsSponsoredCarousel = (
  props: ProductDetailsSponsoredCarouselProps
) => {
  const {
    appMode,
    productId,
    store,
    cartProducts,
    cartIsOpen,
    cartStoreId,
    mode = 'ideal-width',
    attemptToAddToCart,
    onDeleteFromCart,
    specials,
    isProductInStock,
  } = props;

  const { isLoading, instance } = useSimilarProductsRow({
    productId,
    searchAttributes: ['*'],
    storeId: Number(store.id),
  });

  const rowName = isProductInStock
    ? instance?.title || 'You may also like'
    : 'Other products you may like';

  /**
   * If the count of all the sponsored products is equal to the count
   * of all the products, then all the products are sponsored.
   */
  const isRowSponsored = useMemo(() => {
    if (!instance || !instance.products.length) {
      return false;
    }

    return (
      instance.products.filter((product) => product.isSponsored).length ===
      instance.products.length
    );
  }, [instance]);

  if (!isLoading && (!instance || instance.products.length === 0)) {
    return <></>;
  }

  return (
    <MenuProductCarousel
      isLoading={isLoading}
      name={rowName}
      mode={mode}
      listView={false}
      variant={isRowSponsored ? 'sponsored' : 'default'}
      mb={24}
    >
      {instance?.products.map((product, index) => (
        <CardCarousel.Card
          key={`row-ad-${product.objectId}`}
          width="100%"
          widthMobile="100%"
        >
          <ProductCardWithTracking
            algoliaIndexName={`menu-products-${config.algoliaEnv}`}
            carouselView={true}
            columnPosition={index}
            menuRowName={rowName}
            productLocation={'PDP'}
            product={product}
            rowPosition={0}
            listView={false}
            cartIsOpen={cartIsOpen}
            cartProducts={cartProducts}
            store={store}
            appMode={appMode}
            attemptToAddToCart={attemptToAddToCart}
            deleteCartItem={(item, priceId) => {
              onDeleteFromCart(item, priceId);
            }}
            cartStoreId={cartStoreId}
            isRowSponsored={isRowSponsored}
            specials={specials}
          />
        </CardCarousel.Card>
      ))}
    </MenuProductCarousel>
  );
};
